import React, { useContext, useState, useMemo } from 'react';
import { Input, Badge, Tag, Tooltip, Typography, Space, Skeleton, Empty, Button } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { TriageContext } from '../../contexts/TriageContext';
import { classTagMap, classTooltipMap } from '../triageMaps';
import { brandColors } from '../../theme/antd-theme';
import type { LocationWithCounts } from '../../types/triage';

interface LocationPaneProps {
  locations: LocationWithCounts[];
  loading: boolean;
  onCollapse?: () => void;
  // When set, the pane shows issue rollups (one row per incident) instead of
  // raw ticket counts. Keeps the location badge consistent with the IssueList
  // shown in the middle pane. 'tasks' = open work-task counts (needs taskCounts).
  mode?: 'triage' | 'issues' | 'tickets' | 'tasks';
  // External selection control. When omitted, falls back to TriageContext
  // (used by /triage and /issues which wrap their tree in TriageProvider).
  // Pages outside the Triage flow (e.g. /tasks) pass these explicitly so the
  // shared pane stays usable without forcing a provider rewrite.
  selectedLocationId?: string | null;
  onSelectLocation?: (id: string) => void;
  // 'tasks' mode rollup. Map of location_id → open task count; undefined
  // locations render at 0.
  taskCounts?: Record<string, number>;
}

const LocationPane: React.FC<LocationPaneProps> = ({
  locations,
  loading,
  onCollapse,
  mode = 'tickets',
  selectedLocationId: selectedLocationIdProp,
  onSelectLocation,
  taskCounts,
}) => {
  // Read context if available; pages outside TriageProvider pass props instead.
  const ctx = useContext(TriageContext);
  const selectedLocationId = selectedLocationIdProp !== undefined ? selectedLocationIdProp : ctx?.selectedLocationId ?? null;
  const selectLocation = onSelectLocation ?? ctx?.selectLocation ?? (() => {});
  const [searchTerm, setSearchTerm] = useState('');

  // Pick the right rollup for the active route. Triage = suggested-only (what
  // the operator still needs to review). Issues = confirmed-only (active work).
  //
  // 2026-05-25 fix: previously fell back to `ticketCounts.total` when the
  // location had no issueCounts row — but a missing row means /api/issues/counts
  // returned zero matching issues for this location (all filtered: active
  // tasks, classifier-confirmed, etc.). Falling back to raw ticket counts
  // showed badges (e.g. "1") that didn't match the empty queue the user got
  // after clicking. Always trust the issues feed in issues/triage modes;
  // 0 means 0.
  const totalFor = (l: LocationWithCounts): number => {
    if (mode === 'tasks') return taskCounts?.[l.location_id] ?? 0;
    if (mode === 'tickets') return l.ticketCounts.total;
    if (!l.issueCounts) return 0;
    if (mode === 'triage') return l.issueCounts.suggested;
    return l.issueCounts.confirmed;
  };

  const byClassFor = (l: LocationWithCounts): Record<number, number> => {
    if (mode === 'tasks') return {};
    if (mode === 'tickets' || !l.issueCounts) return l.ticketCounts.byClass;
    // /triage hides classification breakdowns — the rule classifier's
    // tentative class is just a hint the operator should evaluate fresh.
    // Showing per-class chips on /triage made operators anchor on the
    // suggested grouping before opening the issue.
    if (mode === 'triage') return {};
    return l.issueCounts.byClassConfirmed;
  };

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let locs = term
      ? locations.filter((l) => l.location_name.toLowerCase().includes(term))
      : locations;
    // In triage/issues modes, hide locations whose visible badge would be 0 —
    // clicking them just shows an empty queue. EXCEPT keep the currently-
    // selected location visible even at 0, so the operator can see their
    // own selection state and switch from it (audit 2026-05-25 found
    // mid-session selections vanishing when the last issue was triaged).
    // When the user typed a search term, respect it even for 0-count
    // locations so they can still find a place they expect.
    if (!term && mode !== 'tickets') {
      locs = locs.filter((l) => totalFor(l) > 0 || l.location_id === selectedLocationId);
    }
    return locs.slice().sort((a, b) => totalFor(b) - totalFor(a));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, searchTerm, mode, selectedLocationId]);

  if (loading) {
    return (
      <div style={{ padding: 12 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} active title={false} paragraph={{ rows: 1, width: '80%' }} style={{ marginBottom: 8 }} />
        ))}
      </div>
    );
  }

  const itemStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: '6px 12px',
    cursor: 'pointer',
    background: isSelected ? '#e6f4ff' : 'transparent',
    borderLeft: `3px solid ${isSelected ? brandColors.blue : 'transparent'}`,
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.15s',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '6px 12px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography.Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#8c8c8c' }}>
          Locations
        </Typography.Text>
        {onCollapse && (
          <Button type="text" size="small" icon={<LeftOutlined style={{ fontSize: 10 }} />} onClick={onCollapse} />
        )}
      </div>
      <div style={{ padding: '6px 12px 0' }}>
        <Input.Search
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          size="small"
        />
      </div>

      <div style={{ flex: 1, overflow: 'auto', paddingTop: 4 }}>
        {/* "All Locations" was removed (2026-05-24) — selecting it set
            selectedLocationId=null which made loadIssues / loadTickets
            early-return with an empty list, so the option produced nothing.
            Operator picks a location to populate the queue. */}
        {filtered.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No locations" style={{ marginTop: 24 }} />
        ) : (
          // Native div mapping — antd <List renderItem> was intermittently
          // swallowing clicks in this sider on React 19 + antd 5.29 (operator
          // reported "click does nothing" 2026-05-25). Direct mapping removes
          // the wrapper layer entirely; click handler attaches straight to
          // the row div.
          <div role="list">
            {filtered.map((loc) => {
              const isSelected = selectedLocationId === loc.location_id;
              return (
                <div
                  key={loc.location_id}
                  onClick={() => selectLocation(loc.location_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectLocation(loc.location_id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-current={isSelected ? 'page' : undefined}
                  aria-label={loc.location_name}
                  style={itemStyle(isSelected)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography.Text
                      ellipsis={{ tooltip: loc.location_name }}
                      style={{ maxWidth: 150, fontSize: 13 }}
                    >
                      {loc.location_name}
                    </Typography.Text>
                    <Badge count={totalFor(loc)} overflowCount={9999} style={{ backgroundColor: brandColors.blue }} />
                  </div>
                  {/* wrap=true: the 240px sider used to clip the right edge of
                      overflowing chips (e.g. "TAMP 1", "OOR 3"). Let them wrap
                      to a second line instead. */}
                  <Space size={[4, 2]} wrap style={{ marginTop: 2 }}>
                    {Object.entries(byClassFor(loc)).map(([classId, count]) => {
                      if (count <= 0) return null;
                      const id = Number(classId);
                      const meta = classTagMap[id];
                      const tag = (
                        <Tag
                          key={classId}
                          color={meta?.color}
                          style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px', margin: 0 }}
                        >
                          {meta?.label ?? `#${id}`} {count}
                        </Tag>
                      );
                      return classTooltipMap[id] ? (
                        <Tooltip key={classId} title={classTooltipMap[id]}>{tag}</Tooltip>
                      ) : tag;
                    })}
                  </Space>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationPane;
